import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Lightbulb, Tag, X } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { productApi } from "@/resources/product";
import { useCreateBrandSignal, useGetBrandSignals, useUpdateBrandSignalTags } from "@/resources/brand-signals";
import { handleApiError } from "@/utils";
import { toast } from "@/hooks/use-toast";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";
import { BrandSignalsTable } from "./BrandSignalsTable";

const SIGNAL_CATEGORIES = [
  "Title Strategy",
  "Bullet Strategy",
  "Positioning",
  "Audience",
  "Other",
] as const;

export function BrandSignalsView() {
  const readOnly = useViewerReadOnly();
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const { data: brandAsinsData = [] } = productApi.useGetHeroProductList(selectedBrandId ?? undefined);
  const { data: apiSignals = [], isPending: isSignalsPending } = useGetBrandSignals(selectedBrandId);
  const createSignal = useCreateBrandSignal();
  const updateTags = useUpdateBrandSignalTags();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newHypothesis, setNewHypothesis] = useState("");
  const [newCategory, setNewCategory] = useState("Title Strategy");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState("");

  const heroProducts = useMemo(
    () =>
      brandAsinsData.map((a) => ({
        id: String(a.id),
        name: a.name,
        imageUrl: a.image_url ?? undefined,
        asin: a.asin,
      })),
    [brandAsinsData],
  );

  const resolvedProductIdForTest = heroProducts[0]?.id ?? undefined;
  const { data: heroAsinForTest } = productApi.useGetHeroProductABTests(
    resolvedProductIdForTest,
    selectedBrandId ?? undefined,
  );

  const toggleProduct = (productId: string) => {
    if (productId === "all") {
      setSelectedProducts(["all"]);
      return;
    }
    setSelectedProducts((prev) => {
      const without = prev.filter((p) => p !== "all" && p !== productId);
      if (prev.includes(productId)) return without.length === 0 ? ["all"] : without;
      return [...without, productId];
    });
  };

  const handleUpdateTags = async (signalId: string, tags: string[]) => {
    if (selectedBrandId == null) return;
    try {
      await updateTags.mutateAsync({ brandId: selectedBrandId, signalId, tags });
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenCreateTest = useCallback(
    (signalId: string, hypothesis: string) => {
      if (!heroAsinForTest || !resolvedProductIdForTest) return;
      const state: CreateABTestLocationState = {
        heroAsin: heroAsinForTest,
        productId: Number(resolvedProductIdForTest),
        prefillFromSignal: { signalId, statement: hypothesis },
        defaultSource: "brand-signal",
        returnTo: window.location.pathname + window.location.search,
      };
      navigate(RoutePath.CreateABTest, { state });
    },
    [heroAsinForTest, resolvedProductIdForTest, navigate],
  );

  const handleAddNewTag = () => {
    const tag = newTagInput.trim();
    if (tag && !newTags.includes(tag)) {
      setNewTags((prev) => [...prev, tag]);
    }
    setNewTagInput("");
  };

  const handleAddSignal = async () => {
    if (!newHypothesis.trim() || selectedBrandId == null) return;
    const productNames = selectedProducts.includes("all")
      ? heroProducts.map((p) => p.name)
      : heroProducts.filter((p) => selectedProducts.includes(p.id)).map((p) => p.name);
    try {
      await createSignal.mutateAsync({
        brandId: selectedBrandId,
        body: { hypothesis: newHypothesis.trim(), category: newCategory, products: productNames },
      });
      setNewHypothesis("");
      setSelectedProducts([]);
      setNewTags([]);
      setNewTagInput("");
      setAddModalOpen(false);
      toast({ title: "Signal Added", description: "Your hypothesis has been added for tracking." });
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <>
      {/* {!readOnly && (
        <div className="px-6 pt-4 flex justify-end">
          <Button size="sm" onClick={() => setAddModalOpen(true)} disabled={isSignalsPending}>
            <Plus className="h-4 w-4" /> Add Signal
          </Button>
        </div>
      )} */}

      <BrandSignalsTable
        signals={apiSignals}
        isLoading={isSignalsPending}
        onUpdateTags={handleUpdateTags}
        onCreateTest={!readOnly && heroProducts.length > 0 ? handleOpenCreateTest : undefined}
      />

      <Dialog open={!readOnly && addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md bg-card" style={{ borderRadius: "8px" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" /> Add Brand Signal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Hypothesis</label>
              <Textarea
                value={newHypothesis}
                onChange={(e) => setNewHypothesis(e.target.value)}
                placeholder="e.g. Benefit-led titles outperform ingredient-led titles for tinted sunscreens"
                className="resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Category</label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger style={{ borderRadius: "8px" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: "8px" }}>
                  {SIGNAL_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Product Tags</label>
              <p className="text-xs text-muted-foreground mb-2">
                Category tags help Signal AI match this signal to new ASINs with similar attributes.
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {newTags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-[10px] font-semibold text-foreground bg-muted border-border gap-1">
                    {tag}
                    <button onClick={() => setNewTags((prev) => prev.filter((t) => t !== tag))} className="text-muted-foreground hover:text-foreground">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddNewTag(); } }}
                  placeholder="e.g. Mineral SPF, Tinted, Lightweight..."
                  className="h-8 text-sm"
                />
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={handleAddNewTag} disabled={!newTagInput.trim()}>
                  <Tag className="h-3 w-3" /> Add
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Relevant Products</label>
              <p className="text-xs text-muted-foreground mb-2">
                Signal AI will track relevant tests on these products to validate this hypothesis.
              </p>
              <div className="space-y-2 max-h-[160px] overflow-y-auto border border-border rounded-[8px] p-2.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedProducts.includes("all")}
                    onCheckedChange={() => toggleProduct("all")}
                  />
                  <span className="text-sm font-medium text-foreground">All Products</span>
                </label>
                {heroProducts.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={selectedProducts.includes("all") || selectedProducts.includes(p.id)}
                      onCheckedChange={() => toggleProduct(p.id)}
                    />
                    <span className="text-sm text-foreground">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline-on-white" onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleAddSignal()} disabled={!newHypothesis.trim() || createSignal.isPending}>
              Add Signal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
